<template>
    <div class="relative" :class="[classes]">
        <small v-if="smallLabel">
            <span class="label">{{ smallLabel }}</span>
        </small>
        <div class="relative"
             :class="{'mb-2': examples}">
            <input
                :type="type"
                class="form-control"
                :class="[inputClasses]"
                :placeholder="placeholder"
                :maxlength="maxlength"
                :max="maxNumber"
                :min="minNumber"
                autocomplete="nope"
                ref="taller"
                v-model.trim="query"
                @focus="focusEvent"
                @keypress="inKeypress"
                @keydown.down="down"
                @keydown.up="up"
                @keydown.enter.prevent="hit($event)"
                @keydown.esc="reset"
                @input="(e) => { this.query = e.target.value; this.update(e) }"
            />
            <spinner class="absolute right-1 top-[50%] translate-y-[-50%]"
                     :w="20"
                     :h="20"
                     v-if="loading"/>
            <svg xmlns="http://www.w3.org/2000/svg"
                 width="16"
                 height="16"
                 viewBox="0 0 16 16"
                 class="reset-search"
                 @click="resetSearch"
                 v-if="!loading && query && withClearField">
                <path fill="#9b9b9c" fill-rule="nonzero"
                      d="M14 .825L13.175 0 7 6.175.825 0 0 .825 6.175 7 0 13.175.825 14 7 7.825 13.175 14l.825-.825L7.825 7z"/>
            </svg>
            <template v-if="preFetchArray.length && hasItems">
                <i class="material-icons chevron" @click="showList = false" v-if="showList">expand_less</i>
                <i class="material-icons chevron" @click="showList = true" v-else>expand_more</i>
            </template>
        </div>

        <small v-if="examples" class="block text-gray-400 text-[10px] -mt-1 mb-2">{{ examples }}</small>

        <ul
            v-if="hasItems && showList"
            class="absolute w-full max-h-[200px] shadow-md left-0 bg-white overflow-y-auto overflow-x-hidden m-0 p-2 z-10"
            :class="[smallLabel ? 'top-[70px]' : 'top-[46px]']">
            <template v-for="(item, index) in items">
                <li :key="`type-head-keu-${item}-${index}`"
                    class="my-2 mb-0 cursor-pointer hover:text-primary-hover leading-snug"
                    :class="{'active': activeClass(index)}"
                    @click="hitFromList(index)"
                    v-html="highlighting(item, vue)">
                </li>
            </template>
        </ul>

        <div
            class="border-red-500 bg-red-100 text-red-900 absolute w-full shadow-md left-0 m-0 p-2 z-10"
            :class="[smallLabel ? 'top-[70px]' : 'top-[46px]']"
            v-if="notFound && !loading && !isEmpty && query && query.length >= minChars && wasRequest"
            v-html="NoResultText"></div>
    </div>
</template>

<script>
    import Mark from "mark.js";
    import Vuex from "vuex";

    export default {
        name: "type-ahead",
        props: {
            examples: String,
            smallLabel: String,
            minNumber: {
                type: Number,
                default: 1
            },
            values: {
                type: [Array, Object],
                default: () => {
                    return [] || {};
                }
            },
            maxNumber: {
                type: Number,
                default: 99999
            },
            type: {
                type: String,
                default: "text"
            },
            keypress: {
                type: Function
            },
            selectFirst: {
                required: false,
                type: Boolean,
                default: false
            },
            queryParamName: {
                required: false,
                type: String,
                default: ":keyword"
            },
            limit: {
                required: false,
                type: Number,
                default: 10
            },
            minChars: {
                required: false,
                type: Number,
                default: 1
            },
            maxlength: {
                required: false,
                type: Number,
                default: 5
            },
            code: {
                required: false,
                type: String
            },
            delayTime: {
                required: false,
                default: 500,
                type: Number
            },
            placeholder: {
                required: false,
                type: String
            },
            showSearchingFlag: {
                required: false,
                default: false,
                type: Boolean
            },
            NoResultText: {
                required: false,
                default: "No results found",
                type: String
            },
            SearchingText: {
                required: false,
                default: "Searching...",
                type: String
            },
            classes: {
                required: false,
                type: String
            },
            inputClasses: {
                type: String
            },
            value: {
                required: false,
                type: String,
                default: ""
            },
            onHit: {
                required: false,
                type: Function,
                default: (item) => {
                    this.query = item;
                    this.hitBool = true;
                }
            },
            highlighting: {
                required: false,
                type: Function,
                default: (item, vue) => {
                    let string = item.title.toString();
                    if (vue.query) {
                        setTimeout(() => {
                            vue.markInstance.unmark({
                                done: () => {
                                    vue.markInstance.mark(vue.query, {
                                        element: "strong"
                                    });
                                }
                            });
                        }, 5);
                    } else {
                        vue.markInstance.unmark();
                    }
                    return string;
                }
            },
            render: {
                required: false,
                type: Function,
                default: function (items) {
                    return items;
                }
            },
            getResponse: {
                required: false,
                type: Function
            },
            objectArray: {
                required: false,
                type: Array
            },
            preFetchArray: {
                type: Array,
                default: () => {
                    return [];
                }
            },
            withClearField: {
                type: Boolean,
                default: false
            }
        },
        methods: {
            focusEvent () {
                if (this.items.length) {
                    this.showList = true;
                }
            },
            inKeypress (event) {
                if (this.keypress) {
                    this.keypress(event);
                }
                return true;
            },
            objectUpdate () {
                let filtered = this.objectArray.filter((entity) =>
                    entity.toLowerCase().includes(this.query.toLowerCase())
                );
                this.data = this.limit ? filtered.slice(0, this.limit) : filtered;
                this.items = this.render(this.limit ? this.data.slice(0, this.limit) : this.data, this);

                this.current = -1;

                if (this.selectFirst) {
                    this.down();
                }
            },
            update (event) {
                if (!this.query) {
                    this.items = this.preFetchArray;
                    this.showList = true;
                    this.loading = false;
                    return;
                }

                this.lastTime = event.timeStamp;
                if (!this.query) {
                    return this.reset();
                }

                if (this.minChars && this.query.length < this.minChars) {
                    this.loading = false;
                    return;
                }

                this.loading = true;
                this.notFound = false;

                setTimeout(() => {
                    if (!this.query) {
                        this.items = this.preFetchArray;
                        this.loading = false;
                        return;
                    }

                    if (this.lastTime - event.timeStamp === 0) {
                        if (this.objectArray) {
                            return this.objectUpdate();
                        }

                        this.loading = true;

                        const re = new RegExp(this.queryParamName, "g");
                        if (this.values.length) {
                            let _data = [];
                            this.values.forEach((item) => {
                                if (
                                    item.title.toLowerCase().startsWith(this.query) ||
                                    item.title.startsWith(this.query)
                                ) {
                                    _data.push(item);
                                }
                            });
                            this.values.forEach((item) => {
                                if (
                                    item.title.toLowerCase().indexOf(this.query) >= 0 ||
                                    item.title.indexOf(this.query) >= 0
                                ) {
                                    if (!_data.includes(item)) {
                                        _data.push(item);
                                    }
                                }
                            });
                            this.items = this.render(_data, this);
                            this.current = -1;
                            this.loading = false;
                            this.showList = true;

                            setTimeout(() => {
                                this.$refs.taller.focus();
                            }, 100);
                        } else {
                            let params = {
                                query: this.query
                            };
                            if (this.code) {
                                params.code = this.code;
                            }
                            this.$store.dispatch("widgetData/getValues", params).then((response) => {
                                if (this.query) {
                                    this.wasRequest = true;

                                    let data = this.getResponse(response);
                                    if (data.length) {
                                        this.data = this.limit ? data.slice(0, this.limit) : data;
                                        this.items = this.render(this.limit ? data.slice(0, this.limit) : data, this);
                                        this.showList = true;
                                    } else {
                                        this.notFound = true;
                                        this.showList = false;
                                        this.items = [];
                                    }

                                    this.current = -1;
                                    this.loading = false;

                                    setTimeout(() => {
                                        this.$refs.taller.focus();
                                    }, 100);
                                }
                            });
                        }
                    }
                }, this.delayTime);
            },
            setActive (index) {
                this.current = index;
            },
            activeClass (index) {
                return this.current === index;
            },
            hitFromList (index) {
                this.current = index;
                this.hit();
            },
            hit () {
                if (this.current !== -1) {
                    this.onHit(this.items[this.current], this, this.current);
                }
                this.reset();
                this.notFound = false;
            },
            up () {
                if (this.current <= this.items.length - 1 && this.current !== 0) {
                    this.current--;
                }
            },
            down () {
                if (this.current < this.items.length - 1) {
                    this.current++;
                }
            },
            reset () {
                this.showList = false;
                this.loading = false;
                this.items = [];
            },
            resetSearch () {
                this.query = "";
                this.items = this.preFetchArray;
                this.showList = false;
                this.$emit("reset");
            }
        },
        watch: {
            value (value) {
                this.query = this.query !== value ? value : this.query;
            },
            query (value) {
                this.$emit("input", value);
            },
            preFetchArray () {
                if (this.preFetchArray.length) {
                    this.items = this.preFetchArray;
                }
            }
        },
        computed: {
            vue () {
                return this;
            },
            hasItems () {
                return this.items.length > 0;
            },
            isEmpty () {
                return this.query === "";
            }
        },
        data () {
            return {
                items: [],
                query: "",
                current: -1,
                loading: false,
                lastTime: 0,
                data: [],
                hitBool: false,
                showList: false,
                wasRequest: false,
                markInstance: new Mark("ul.dropdown-menu-list"),
                notFound: false
            };
        },
        beforeMount () {
            if (this.preFetchArray.length) {
                this.items = this.preFetchArray;
            }
        },
        mounted () {
            this.query = this.value;
            document.addEventListener("click", (e) => {
                if (!this.$el.contains(e.target)) {
                    this.showList = false;
                }
            });
            if (this.objectArray) {
                this.objectArray.sort();
            }
        }
    };
</script>
